import Typography from '@mui/material/Typography';
import React from 'react';

import { Box } from '@mui/material';
import { PermissionData } from '../../api/types';
import { PermissionGainAccessItem } from './PermissionGainAccessItem';

const getPermissionData = ({
    accessPointCode,
    permissionInfo,
    translations,
}: {
    accessPointCode: string;
    permissionInfo: PermissionData;
    translations: Record<string, string>;
}) => [
    {
        permissionType: 'card',
        situation: permissionInfo.cardSituation,
        reason: translations.PERMISSIONS_MISSING_CARD_TITLE,
        subtitle: translations.PERMISSIONS_MISSING_CARD_SUBTITLE,
        link: permissionInfo.cardLink,
    },
    {
        permissionType: 'elock',
        situation: permissionInfo.eLockSituation,
        reason: translations.PERMISSIONS_MISSING_ELOCKS_TITLE,
        subtitle: translations.PERMISSIONS_MISSING_ELOCKS_SUBTITLE,
        link: permissionInfo.eLockLink,
    },
    {
        permissionType: 'dosimeter',
        situation: permissionInfo.dosimeterSituation,
        reason: translations.PERMISSIONS_MISSING_DOSIMETER_TITLE,
        subtitle: translations.PERMISSIONS_MISSING_DOSIMETER_SUBTITLE,
        link: permissionInfo.dosimeterLink,
    },
    {
        permissionType: 'request',
        situation: permissionInfo.requestSituation,
        reason: translations.PERMISSIONS_MISSING_ACCESS_REQUEST_TITLE,
        // eslint-disable-next-line no-nested-ternary
        subtitle: permissionInfo.requestCanBeCreated
            ? permissionInfo.requestExists
                ? translations.PERMISSIONS_EXISTING_ACCESS_REQUEST_SUBTITLE
                : translations.PERMISSIONS_MISSING_ACCESS_REQUEST_SUBTITLE
            : translations.PERMISSIONS_MISSING_ACCESS_REQUEST_UNAVAILABLE,
        link: `/accesspoint/${accessPointCode}/permissions/${permissionInfo.permissionCode}/request-access`,
        disabled: !permissionInfo.requestCanBeCreated,
    },
    {
        permissionType: 'contract',
        situation: permissionInfo.contractSituation,
        reason: translations.PERMISSIONS_MISSING_CONTRACT_TITLE,
        subtitle: translations.PERMISSIONS_MISSING_CONTRACT_SUBTITLE,
        link: permissionInfo.contractLink,
    },
    {
        permissionType: 'impact',
        situation: permissionInfo.IMPACTSituation,
        reason: translations.PERMISSIONS_MISSING_IMPACT_TITLE,
        subtitle: translations.PERMISSIONS_MISSING_IMPACT_SUBTITLE,
        link: permissionInfo.IMPACTLink,
    },
    {
        permissionType: 'exception',
        situation: permissionInfo.exceptionSituation,
        reason: translations.PERMISSIONS_EXCEPTION_TITLE,
        subtitle: translations.PERMISSIONS_EXCEPTION_SUBTITLE,
        link: 'mailto:access.registration.support@cern.ch',
    },
    {
        permissionType: 'additionalClause',
        situation: permissionInfo.additionalClauseSituation,
        reason: translations.PERMISSIONS_ADDITIONAL_CLAUSE_TITLE,
        subtitle: translations.PERMISSIONS_ADDITIONAL_CLAUSE_SUBTITLE,
        link: 'mailto:access.registration.support@cern.ch',
    },
];

interface PermissionGainAccessProps {
    accessPointCode: string;
    permissionInfo: PermissionData;
    readOnly: boolean;
    translations: Record<string, string>;
}

export const PermissionGainAccess: React.FC<PermissionGainAccessProps> = ({
    accessPointCode,
    readOnly,
    permissionInfo,
    translations,
}) => {
    const situationsForGainingAccess = getPermissionData({
        accessPointCode,
        permissionInfo,
        translations,
    })
        // not all situation keys exist in api data, need to check that situation is boolean false, not falsy, such as undefined
        .filter(({ situation }) => situation === false)
        .map(({ permissionType, reason, subtitle, link, disabled }) => (
            <PermissionGainAccessItem
                requestable={permissionInfo.requestAble}
                reason={reason}
                subtitle={subtitle}
                readOnly={!!(readOnly || disabled)}
                link={
                    permissionType === 'request' && (permissionInfo.requestExists || !permissionInfo.requestAble)
                        ? null
                        : link
                }
                translations={translations}
                key={reason}
            />
        ));

    if (!situationsForGainingAccess.length) {
        return null;
    }

    return (
        <>
            <Typography variant="subtitle2" gutterBottom>
                {translations.PERMISSIONS_GAIN_ACCESS_TITLE}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>{situationsForGainingAccess}</Box>
        </>
    );
};
